import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { colorSet } from 'src/app/models/global-classes';
import { parseColor, ParsedColor } from 'src/app/shared/utils/ui/color-utils';
export enum DropdownItemActionTypeEnum {
  normal = 'normal',
  portal = 'portal',
}

export class DropdownItem {
  label: string;
  iconSvg?: string;
  icon?: string;
  type?: DropdownItemActionTypeEnum;
  colorSet?: string | colorSet = 'gray-700';
  colorSetHover?: string | colorSet = 'primary';
  action?: () => void | Promise<void>;
  routerLink?: string | any[];
  href?: string;
  target?: '_blank' | '_self' | '_parent' | '_top' | 'framename' | undefined;
  disabled?: boolean;
  forceHide?: boolean;
  isActive?: boolean = false;
  hidden?: boolean;

  constructor(values?: DropdownItem) {
    Object.assign(this, values);
  }
}

@Component({
  selector: 'app-dynamic-dropdown',
  templateUrl: './dynamic-dropdown.component.html',
  styleUrls: ['./dynamic-dropdown.component.css'],
})
export class DynamicDropdownComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  dropdownItemActionTypeEnum = DropdownItemActionTypeEnum;

  @Output() DropdownEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() disabled: boolean = false;
  @Input() dropdownItems: DropdownItem[] = [];
  @Input() hoverTrigger: boolean = false;
  @Input() container: 'body' | null = 'body';

  public dropdownId = 'hs-dropdown-' + Math.random().toString(36).substr(2, 9);
  public isOpen: boolean = false;

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngOnInit() {}

  ngAfterViewInit() {
    // Manual control implemented, no autoInit needed
  }

  ngOnDestroy() {
    // Clean up dropdown state to prevent reference issues
    this.isOpen = false;
  }

  onOpenChange(isOpen: boolean) {
    this.isOpen = isOpen;
    this.cdr.detectChanges();
  }

  onDropdownEvent(value: any) {
    this.DropdownEvent.emit(value);
  }

  /**
   * Handles the item click action
   */
  handleItemClick(item: DropdownItem, event?: Event): void {
    if (item.disabled) {
      event?.preventDefault();
      return;
    }
    if (item.action) {
      item.action();
    }
  }

  /**
   * Parses a color string into its components
   * Handles: "text-green", "text-green-500", "green", "green-500", "primary"
   */
  private parseColor(colorString: string | colorSet | undefined): ParsedColor {
    return parseColor(colorString);
    // if (!colorString) {
    //   return { base: 'gray', shade: '700', fullColor: 'gray-700' };
    // }

    // let color = String(colorString).trim();

    // // Remove 'text-' prefix if present
    // if (color.startsWith('text-')) {
    //   color = color.substring(5);
    // }

    // // Remove 'bg-' prefix if present
    // if (color.startsWith('bg-')) {
    //   color = color.substring(3);
    // }

    // // Split by '-' to get base and shade
    // const parts = color.split('-');
    // const base = parts[0];
    // const shade = parts[1];

    // return {
    //   base,
    //   shade,
    //   fullColor: shade ? `${base}-${shade}` : base
    // };
  }

  /**
   * Gets complete set of classes for a dropdown item
   * Returns object with text, hoverText, and hoverBg classes
   */
  getItemClasses(item: DropdownItem): string {
    const defaultColor = this.parseColor(item.colorSet);
    const hoverColor = this.parseColor(item.colorSetHover || item.colorSet);

    const classes = [
      // Default text color
      `text-${defaultColor.fullColor}`,

      // Hover text color
      `hover:text-${hoverColor.fullColor}`,

      // Hover background (always use light shade - 50)
      // If hoverColor has a shade, use base-50, otherwise use color-50
      `hover:bg-${hoverColor.base}-50`,

      // Transition for smooth color changes
      'transition-colors',
      'duration-150',
    ];

    return classes.join(' ');
  }

  /**
   * Gets background hover class separately for more control
   */
  getItemBgHoverClass(item: DropdownItem): string {
    const hoverColor = this.parseColor(item.colorSetHover || item.colorSet);
    return `hover:bg-${hoverColor.base}-50`;
  }

  /**
   * Gets complete set of classes for portal items
   * Uses custom class naming convention: text-hover-{color} and bg-hover-light-{color}
   */
  getPortalClasses(item: DropdownItem): string {
    const defaultColor = this.parseColor(item.colorSet);
    const hoverColor = this.parseColor(item.colorSetHover || item.colorSet);

    const classes = [
      // Default text color
      `text-${defaultColor.fullColor}`,

      // Custom hover classes (assumes these are defined in CSS)
      `text-hover-${hoverColor.base}`,
      `bg-hover-light-${hoverColor.base}`,

      // Transition
      'transition-colors',
      'duration-150',
    ];

    return classes.join(' ');
  }

  /**
   * Backward compatibility methods (optional - can be removed if template is updated)
   */
  getDropdownItemClass(
    colorString: string | colorSet | undefined,
    isHoverBg: boolean = false,
    isHoverText: boolean = false
  ): string {
    const parsed = this.parseColor(colorString);

    if (isHoverBg) {
      return `hover:bg-${parsed.base}-50`;
    } else if (isHoverText) {
      return `hover:text-${parsed.fullColor}`;
    } else {
      return `text-${parsed.fullColor}`;
    }
  }

  getPortalColorClass(
    colorString: string | colorSet | undefined,
    isHover: boolean = false
  ): string {
    const parsed = this.parseColor(colorString);

    if (isHover) {
      return `text-hover-${parsed.base} bg-hover-light-${parsed.base}`;
    } else {
      return `text-${parsed.fullColor}`;
    }
  }
}
